// EcomNewCustomersChart.tsx
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  TooltipComponent,
  GridComponent,
  LegendComponent
} from 'echarts/components';
import { useAppContext } from 'providers/AppProvider';
import { OrderData } from 'api/dashboard/_request';
import dayjs from 'dayjs';
import { useMemo } from 'react';

echarts.use([TooltipComponent, GridComponent, LegendComponent, BarChart]);

type Props = { data: OrderData[]; range: 'today' | 'week' | 'month' | 'year' };

const EcomNewCustomersChart = ({ data, range }: Props) => {
  const { getThemeColor } = useAppContext();

  const { labels, amountSeries, customerSeries } = useMemo(() => {
    const now = dayjs();
    const points: string[] = [];

    if (range === 'today') {
      for (let h = 0; h < 24; h += 3)
        points.push(now.startOf('day').add(h, 'hour').format('YYYY-MM-DD-HH'));
    } else if (range === 'year') {
      for (let m = 0; m < 12; m++)
        points.push(now.startOf('year').add(m, 'month').format('YYYY-MM'));
    } else {
      const start =
        range === 'week'
          ? now.subtract(6, 'day').startOf('day')
          : now.startOf('month');
      const interval = 3;
      for (let d = 0; d <= 30; d += interval)
        points.push(start.add(d, 'day').format('YYYY-MM-DD'));
    }

    const amount: Record<string, number> = {};
    const customers: Record<string, Set<string>> = {};

    points.forEach(p => {
      amount[p] = 0;
      customers[p] = new Set();
    });

    data.forEach(o => {
      const key =
        range === 'today'
          ? dayjs(o.createdAt).format('YYYY-MM-DD-HH')
          : range === 'year'
          ? dayjs(o.createdAt).format('YYYY-MM')
          : dayjs(o.createdAt).format('YYYY-MM-DD');

      amount[key] = (amount[key] || 0) + o.finalAmount;
      customers[key]?.add(o.userId);
    });

    const lbl = points.map(p =>
      range === 'today'
        ? dayjs(p, 'YYYY-MM-DD-HH').format('HH')
        : range === 'year'
        ? dayjs(p).format('MMM')
        : dayjs(p).format('DD-MMM')
    );

    return {
      labels: lbl,
      amountSeries: points.map(p => amount[p] || 0),
      customerSeries: points.map(p => customers[p]?.size || 0)
    };
  }, [data, range]);

  const option = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: getThemeColor('body-highlight-bg'),
      borderColor: getThemeColor('border-color'),
      textStyle: { color: getThemeColor('light-text-emphasis') },
      borderWidth: 1,
      padding: 10
    },
    legend: {
      data: ['Amount', 'Customers'],
      textStyle: { color: getThemeColor('secondary-color'), fontSize: 11 }
    },
    grid: { left: 10, right: 10, top: 50, bottom: 40, containLabel: true },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: {
        color: getThemeColor('secondary-color'),
        fontSize: 10,
        rotate: range === 'month' || range === 'year' ? 30 : 0,
        interval: 0
      },
      axisLine: { lineStyle: { color: getThemeColor('secondary-bg') } },
      axisTick: { show: false }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: getThemeColor('secondary-bg') } }
    },
    series: [
      {
        name: 'Amount',
        type: 'bar',
        data: amountSeries,
        itemStyle: { color: getThemeColor('primary'), borderRadius: 4 },
        barWidth: '30%'
      },
      {
        name: 'Customers',
        type: 'bar',
        data: customerSeries,
        itemStyle: { color: getThemeColor('success'), borderRadius: 4 },
        barWidth: '30%'
      }
    ]
  };

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      style={{ height: 250, width: '100%' }}
    />
  );
};

export default EcomNewCustomersChart;
